// dm-core.js – reine Logik, keine DOM-Zugriffe. Läuft im Browser (Theme-Asset, ES-Modul) UND in Node (scripts/*).
// Eine Quelle für: Preis, Ausschnitt (Crop), Geometrie der Vorschau, Konfigurations-JSON, Druckauftrag.
export const VERSION = 'dm-1.0.0';

export const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
export const stepUp = (mm, step = 100) => Math.ceil(mm / step) * step;
const lerpTable = (t, x) => { if (x <= t[0][0]) return t[0][1]; for (let i = 1; i < t.length; i++) if (x <= t[i][0]) { const [a, pa] = t[i - 1], [b, pb] = t[i]; return pa + ((pb - pa) * (x - a)) / (b - a); } return t[t.length - 1][1]; };

// ---------- Formate ----------
export const SHOWER_PANELS = { single: ['back'], corner: ['left', 'back'], niche: ['left', 'back', 'right'] };
export function panelsFor(room, format, dims) {
  // dims: Küche {width_mm,height_mm}; Dusche {back:{w,h}, left:{w,h}, right:{w,h}} oder {width_mm,height_mm} für alle gleich
  if (room === 'kitchen') return [{ id: 'back', order: 0, width_mm: Math.round(dims.width_mm), height_mm: Math.round(dims.height_mm) }];
  return SHOWER_PANELS[format].map((id, order) => {
    const d = dims[id] || dims;
    return { id, order, width_mm: Math.round(d.w ?? d.width_mm), height_mm: Math.round(d.h ?? d.height_mm) };
  });
}

// ---------- Preis (Spiegel von data/pricing.json; Server-Wahrheit ist der Variantenpreis) ----------
export function priceKitchen(P, w_mm, h_mm) {
  const c = P.kitchen.custom, ws = stepUp(clamp(w_mm, c.width_mm.min, c.width_mm.max)), hs = stepUp(clamp(h_mm, c.height_mm.min, c.height_mm.max));
  // Folie nach Fläche (28.10.2026): 10-cm-Stufe × per_sqm €, auf volle Euro aufrunden − 0,10, mindestens min_price
  if (c.per_sqm) return { price: Math.max(c.min_price, Math.ceil((ws * hs * c.per_sqm) / 1e6) - 0.1), step: `${ws}x${hs}` };
  return { price: Math.round(lerpTable(c.width_anchor_prices_at_600mm_height, ws) * Math.max(1, hs / 600)), step: `${ws}x${hs}` };
}
// Dusche: Preis nach GESAMTbreite aller Wände (10-cm-Stufe) × Höhe (10-cm-Stufe) → genau eine Matrix-Variante je Format.
export function priceShower(P, format, panels) {
  const c = P.shower.custom, f = P.shower.formats[format], n = panels.length;
  const Ws = stepUp(panels.reduce((s, p) => s + clamp(p.width_mm, c.panel_width_mm.min, c.panel_width_mm.max), 0));
  const Hs = stepUp(clamp(Math.max(...panels.map((p) => p.height_mm)), c.panel_height_mm.min, c.panel_height_mm.max));
  return { price: Math.max(199 * n, Math.round((299 * (Ws / 1000) * (Hs / 1000)) / 2.1)) - f.bundle_discount, step: `${Ws}x${Hs}` };
}
export function extrasTotal(P, ex) {
  return (ex.sockets || 0) * P.extras.socket_cutout.price + (ex.fixture_cutouts || 0) * P.extras.fixture_cutout.price + (ex.mounting_kit ? P.extras.mounting_kit.price : 0);
}

// ---------- Ausschnitt: Motiv läuft durchgehend über alle Platten ----------
// pos: 0..100 (0 = links/oben). Rückgabe: Crop je Platte in Pixeln des Originals.
// pxPerMm (Muster-Edition): Fläche im festen Maßstab → Ausschnitt in echter Größe statt „Bild füllen“, damit die Fliesen so groß wirken wie im Druck.
export function computeCrops(srcW, srcH, panels, posX = 50, posY = 50, pxPerMm = 0) {
  const T = panels.reduce((s, p) => s + p.width_mm, 0), H = Math.max(...panels.map((p) => p.height_mm));
  const A = T / H, S = srcW / srcH;
  let cx, cy, cw, ch;
  if (pxPerMm && T * pxPerMm <= srcW + 0.5 && H * pxPerMm <= srcH + 0.5) { cw = Math.min(srcW, T * pxPerMm); ch = Math.min(srcH, H * pxPerMm); cx = (srcW - cw) * (posX / 100); cy = (srcH - ch) * (posY / 100); }
  else if (S > A) { ch = srcH; cw = srcH * A; cx = (srcW - cw) * (posX / 100); cy = 0; }
  else { cw = srcW; ch = srcW / A; cx = 0; cy = (srcH - ch) * (posY / 100); }
  const k = cw / T; // px pro mm
  let off = 0;
  return panels.map((p) => {
    const r = { x: +(cx + off * k).toFixed(1), y: +(cy + ((H - p.height_mm) / 2) * k).toFixed(1), w: +(p.width_mm * k).toFixed(1), h: +(p.height_mm * k).toFixed(1) };
    off += p.width_mm;
    return { ...p, crop: r, effective_dpi: +(r.w / (p.width_mm / 25.4)).toFixed(1) };
  });
}

// ---------- Geometrie Vorschau ----------
export function homography(w, h, q) {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q;
  const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2, sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
  const den = dx1 * dy2 - dx2 * dy1, g = (sx * dy2 - dx2 * sy) / den, hh = (dx1 * sy - sx * dy1) / den;
  const a = x1 - x0 + g * x1, b = x3 - x0 + hh * x3, d = y1 - y0 + g * y1, e = y3 - y0 + hh * y3;
  return `matrix3d(${a / w},${d / w},0,${g / w},${b / h},${e / h},0,${hh / h},0,0,1,0,${x0},${y0},0,1)`;
}
const lerp2 = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
export const quadSize = (q) => { const L = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]); return [Math.round((L(q[0], q[1]) + L(q[3], q[2])) / 2), Math.round((L(q[0], q[3]) + L(q[1], q[2])) / 2)]; };
export function subQuadX(q, a, b) { return [lerp2(q[0], q[1], a), lerp2(q[0], q[1], b), lerp2(q[3], q[2], b), lerp2(q[3], q[2], a)]; }
export function bilinear(q, u, v) { return [0, 1].map((k) => (1 - v) * ((1 - u) * q[0][k] + u * q[1][k]) + v * ((1 - u) * q[3][k] + u * q[2][k])); }
// Welcher Raum für welches Format
export function roomFor(room, format, kitchenStyle = 'hell') {
  if (room === 'kitchen') return 'kueche-' + kitchenStyle;
  return format === 'corner' ? 'dusche-ecke' : 'dusche-nische';
}
// Liefert pro sichtbarer Wand: Quad + CSS-Hintergrund, der EXAKT den Druck-Crop zeigt (WYSIWYG).
export function previewFaces(roomDef, room, format, panels, srcW, srcH) {
  const faces = [], blanks = [];
  const byId = Object.fromEntries(panels.map((p) => [p.id, p]));
  roomDef.walls.forEach((w) => {
    const p = byId[w.id];
    if (!p) { blanks.push(w.q); return; }
    let q = w.q;
    if (room === 'kitchen') { const f = clamp(p.width_mm / roomDef.real_width_mm, 0.08, 1), a = (1 - f) / 2; blanks.push(w.q); q = subQuadX(w.q, a, a + f); }
    // Die Fläche fw × fh ist nur der Ausgangsraum der Homographie, ihr Seitenverhältnis folgt der Pixelgröße der Wand im Foto,
    // nicht dem Druckmaß. Darum getrennt skalieren, damit der Ausschnitt (crop) die Fläche exakt füllt – sonst endet das
    // Motiv bei Hochformat-Wänden (Dusche) mitten auf der Wand.
    const [fw, fh] = quadSize(q), kx = fw / p.crop.w, ky = fh / p.crop.h;
    faces.push({ id: w.id, q, w: fw, h: fh, transform: homography(fw, fh, q), bgSize: `${(srcW * kx).toFixed(1)}px ${(srcH * ky).toFixed(1)}px`, bgPos: `${(-p.crop.x * kx).toFixed(1)}px ${(-p.crop.y * ky).toFixed(1)}px`, shade: w.id === 'back' ? 1 : 0.86, panel: p });
  });
  return { faces, blanks };
}

// ---------- Konfiguration (Line-Item-Property _dm) ----------
export const newKey = () => 'c_' + Array.from(crypto.getRandomValues(new Uint8Array(10)), (b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36]).join('');
export function buildConfig(s) {
  // s: {key, room, format, custom, standard_label, price_step, motif{...}, panels(with crop), ui, options, extras, variant_id, total_cents, P}
  const ex = s.extras || {};
  const sockDim = s.P.extras.socket_cutout.types[ex.socket_variant || '1x'];
  const panels = s.panels.map((p, i) => ({
    ...p,
    cutouts: [
      ...(s.room === 'kitchen' && i === 0 ? Array.from({ length: ex.sockets || 0 }, () => ({ type: 'socket', variant: ex.socket_variant || '1x', w_mm: sockDim[0], h_mm: sockDim[1], cx_mm: null, cy_mm: null, source: 'pending' })) : []),
      ...(s.room === 'shower' && p.id === 'back' ? Array.from({ length: ex.fixture_cutouts || 0 }, () => ({ type: 'fixture', variant: 'round', diameter_mm: s.P.extras.fixture_cutout.default_diameter_mm, cx_mm: null, cy_mm: null, source: 'pending' })) : [])
    ]
  }));
  return { v: 1, key: s.key, created_at: new Date().toISOString(), room: s.room, format: { type: s.format, custom: !!s.custom, standard_label: s.standard_label || null, price_step: s.price_step || null }, motif: s.motif, panels, ui: s.ui, options: { mirror: false, grayscale: false, finish: 'glasoptik', material: s.room === 'shower' ? 'platte' : 'folie', ...(s.options || {}) }, extras: { sockets: 0, socket_variant: '1x', fixture_cutouts: 0, mounting_kit: false, ...ex }, price: { variant_id: s.variant_id, expected_total_cents: s.total_cents }, client: { mobile: typeof matchMedia !== 'undefined' && matchMedia('(max-width: 749px)').matches, app_version: VERSION } };
}
export function validateConfig(c, P) {
  const err = [];
  if (c.v !== 1) err.push('VERSION');
  if (!c.panels?.length) err.push('NO_PANELS');
  const lim = c.room === 'kitchen' ? [P.kitchen.custom.width_mm, P.kitchen.custom.height_mm] : [P.shower.custom.panel_width_mm, P.shower.custom.panel_height_mm];
  c.panels?.forEach((p) => { if (p.width_mm < lim[0].min || p.width_mm > lim[0].max) err.push('WIDTH_RANGE_' + p.id); if (p.height_mm < lim[1].min || p.height_mm > lim[1].max) err.push('HEIGHT_RANGE_' + p.id); const cr = p.crop; if (!cr || cr.x < -0.5 || cr.y < -0.5 || cr.x + cr.w > c.motif.source_w + 0.5 || cr.y + cr.h > c.motif.source_h + 0.5) err.push('CROP_OUT_OF_BOUNDS_' + p.id); });
  return err;
}

// ---------- Druckauftrag ----------
export function printJobs(order, lineItem, cfg, opts = {}) {
  const bleed = opts.bleed_mm ?? 3, target = opts.target_dpi ?? 100, pos = opts.position ?? 1;
  return cfg.panels.map((p, i) => {
    const k = p.crop.w / p.width_mm;
    const wb = { x: Math.max(0, p.crop.x - bleed * k), y: Math.max(0, p.crop.y - bleed * k) };
    wb.w = Math.min(cfg.motif.source_w - wb.x, p.crop.w + 2 * bleed * k); wb.h = Math.min(cfg.motif.source_h - wb.y, p.crop.h + 2 * bleed * k);
    const dpi = +(p.crop.w / (p.width_mm / 25.4)).toFixed(1), warnings = [];
    if (dpi < 100) warnings.push('LOW_DPI');
    if (p.cutouts?.some((c) => c.cx_mm == null)) warnings.push('CUTOUT_POSITION_PENDING');
    if (wb.w < p.crop.w + 2 * bleed * k - 0.5 || wb.h < p.crop.h + 2 * bleed * k - 0.5) warnings.push('BLEED_CLAMPED_AT_IMAGE_EDGE');
    return {
      job_id: `${String(order.name).replace('#', '')}-${pos}-${p.id}`, config_key: cfg.key, line_item_id: lineItem.id, sku: lineItem.sku, room: cfg.room, format: cfg.format.type,
      panel_id: p.id, panel_index: i, panel_count: cfg.panels.length, quantity: lineItem.quantity,
      net_mm: { w: p.width_mm, h: p.height_mm }, bleed_mm: bleed, print_mm: { w: p.width_mm + 2 * bleed, h: p.height_mm + 2 * bleed },
      source: { url: cfg.motif.source_url, w: cfg.motif.source_w, h: cfg.motif.source_h, license: cfg.motif.license, credit: cfg.motif.credit },
      crop_src_px: p.crop, crop_src_px_with_bleed: Object.fromEntries(Object.entries(wb).map(([a, b]) => [a, +b.toFixed(1)])),
      effective_dpi: dpi, target_dpi: target, upscale_factor: +(((p.width_mm + 2 * bleed) / 25.4 * target) / wb.w).toFixed(2),
      mirror: !!cfg.options?.mirror, grayscale: !!cfg.options?.grayscale, finish: cfg.options?.finish, material: cfg.options?.material,
      cutouts: p.cutouts || [], warnings
    };
  });
}
